import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Link2
} from 'lucide-react';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import PHSCommandLayout from '@/components/phs/enterprise/PHSCommandLayout';

const API = process.env.REACT_APP_BACKEND_URL;

function CostAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    loadAnalytics();
  }, []);
  
  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/phs/cost-analytics`, { headers });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      toast.error('Failed to load cost analytics');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(value);
  };
  
  return (
    <PHSCommandLayout>
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-teal-600" />
              Cost Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1">Job cost intelligence and benchmarking analysis</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadAnalytics}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-4" />
                <div className="h-12 bg-slate-200 rounded w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !analytics ? (
        <Card className="p-8 text-center">
          <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Analytics Data</h3>
          <p className="text-sm text-slate-500">Complete more jobs to generate analytics</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Jobs Analyzed</p>
                    <p className="text-3xl font-bold text-slate-800">{analytics.summary?.total_jobs_analyzed || 0}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-teal-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Highest Avg Cost Type</p>
                    <p className="text-xl font-bold text-slate-800">{analytics.summary?.highest_avg_type || '-'}</p>
                    <p className="text-sm text-teal-600">{formatCurrency(analytics.summary?.highest_avg_cost)}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Follow-On Jobs</p>
                    <p className="text-3xl font-bold text-slate-800">
                      {analytics.follow_on_frequency?.reduce((acc, f) => acc + f.count, 0) || 0}
                    </p>
                  </div>
                  <Link2 className="h-10 w-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Cost by Job Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Average Cost by Job Type</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.cost_by_job_type?.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No data available</p>
              ) : (
                <div className="space-y-3">
                  {analytics.cost_by_job_type?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-40 truncate">
                        <span className="text-sm font-medium text-slate-700">{item._id || 'Unknown'}</span>
                      </div>
                      <div className="flex-1">
                        <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-teal-500 rounded-full"
                            style={{ 
                              width: `${Math.min(100, (item.avg_cost / (analytics.summary?.highest_avg_cost || 1)) * 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                      <div className="w-24 text-right">
                        <span className="text-sm font-semibold text-slate-800">{formatCurrency(item.avg_cost)}</span>
                      </div>
                      <div className="w-16 text-right">
                        <Badge variant="outline" className="text-[10px]">{item.count} jobs</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Follow-On Frequency */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Follow-On Job Frequency</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.follow_on_frequency?.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No follow-on jobs tracked</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {analytics.follow_on_frequency?.map((item, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-slate-800">{item.count}</div>
                      <div className="text-xs text-slate-500 truncate">{item._id || 'Other'}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(CostAnalyticsPage, 'helpdesk');
